import MessagesPage from "./MessagesPage";
import { supabase } from "../supabaseClient";
import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from "react";
import { useIsMobile } from "../hooks/useIsMobile";
import { COLORS } from "../config/themes";
import Icon from "../components/Icon";
import { GraduationCap, BookOpen, Star, ClipboardList } from "../components/Icon";
import { Badge, Avatar, Button, Modal, InputField, SelectField, StatCard, ExportButtons, TimePicker, AnimatedNumber } from "../components";
import { todayStr, exportToCSV, exportToExcel, studentName, ageGroupColor, ageGroupBg, canEdit, canEditAttendance } from "../utils/helpers";
import { ACTIVITY_TYPES } from "../config/constants";
import { CelebrationOverlay, SeasonalArt } from "../components";
import { updateParentPin, upsertStudent } from "../utils/db";

// ── PIN Setup Card ─────────────────────────────────────────────────────────
const PinSetupCard = ({ currentUser }) => {
  const existingPin = currentUser?.checkin_pin || "";
  const [pin, setPin]             = useState("");
  const [confirm, setConfirm]     = useState("");
  const [editing, setEditing]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);
  const [err, setErr]             = useState("");

  const handleSave = async () => {
    if (pin.length !== 4 || !/^\d{4}$/.test(pin)) { setErr("PIN must be exactly 4 digits."); return; }
    if (pin !== confirm) { setErr("PINs don't match."); return; }
    setSaving(true); setErr("");
    const ok = await updateParentPin(currentUser.id, pin);
    setSaving(false);
    if (ok) {
      setSaved(true); setEditing(false); setPin(""); setConfirm("");
      setTimeout(() => setSaved(false), 3000);
      // update local user object so it reflects immediately
      if (currentUser) currentUser.checkin_pin = pin;
    } else {
      setErr("Failed to save PIN. Please try again.");
    }
  };

  return (
    <div style={{ background: COLORS.card, borderRadius: 16, padding: 20, border: `1px solid ${COLORS.border}`, marginBottom: 28 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: editing ? 16 : 0 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.navy, marginBottom: 2 }}>Check-In PIN</div>
          <div style={{ fontSize: 12, color: COLORS.textMuted }}>
            {existingPin ? "PIN set — enter at the school kiosk to check in/out." : "Set a 4-digit PIN to check in and out at the kiosk."}
          </div>
        </div>
        {!editing && (
          <button onClick={() => setEditing(true)} style={{ padding: "8px 16px", borderRadius: 10, border: `1px solid ${COLORS.border}`, background: COLORS.card, cursor: "pointer", fontSize: 12, fontWeight: 600, color: COLORS.primary, fontFamily: "inherit" }}>
            {existingPin ? "Change PIN" : "Set PIN"}
          </button>
        )}
        {saved && <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.success }}>✓ PIN saved!</span>}
      </div>

      {editing && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <InputField
            label="New 4-digit PIN"
            type="password"
            inputMode="numeric"
            maxLength={4}
            value={pin}
            onChange={e => { setPin(e.target.value.replace(/\D/g, "").slice(0, 4)); setErr(""); }}
            placeholder="Enter 4 digits"
          />
          <InputField
            label="Confirm PIN"
            type="password"
            inputMode="numeric"
            maxLength={4}
            value={confirm}
            onChange={e => { setConfirm(e.target.value.replace(/\D/g, "").slice(0, 4)); setErr(""); }}
            placeholder="Re-enter PIN"
          />
          {err && <div style={{ fontSize: 12, color: COLORS.danger, fontWeight: 600 }}>{err}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <Button onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save PIN"}</Button>
            <Button variant="secondary" onClick={() => { setEditing(false); setPin(""); setConfirm(""); setErr(""); }}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
};

const ParentPortal = memo(({ students, setStudents, classrooms, teachers, events, studentAttendance, activityLog, schoolSettings, currentUser, messages, setMessages, channels, setChannels, users, currentTheme, season, setPage: parentSetPage, schoolId, isDemo, schoolTodayStr }) => {
  const isMobile = useIsMobile();
  const schoolToday = schoolTodayStr ? schoolTodayStr() : todayStr();
  const [view, setView] = useState("home");
  const [selectedChild, setSelectedChild] = useState(null);
  const [logDate, setLogDate] = useState(schoolToday);
  const td = schoolToday;
  const todayAtt = studentAttendance[td] || {};

  // Contact info editing
  const [editingContact, setEditingContact] = useState(false);
  const [contactForm, setContactForm] = useState({});
  const [contactSaving, setContactSaving] = useState(false);
  const [contactSaved, setContactSaved] = useState(false);

  const openContactEdit = (child) => {
    setContactForm({
      guardianName: child.guardianName || "",
      guardianPhone: child.guardianPhone || "",
      guardianEmail: child.guardianEmail || "",
      address: child.address || "",
    });
    setEditingContact(true);
    setContactSaved(false);
  };

  const saveContactInfo = async (child) => {
    if (!contactForm.guardianName.trim() || !contactForm.guardianPhone.trim()) return;
    setContactSaving(true);
    const updated = { ...child, ...contactForm };
    await upsertStudent(updated, schoolId);
    if (setStudents) setStudents(p => p.map(s => s.id === child.id ? { ...s, ...contactForm } : s));
    setContactSaving(false);
    setContactSaved(true);
    setEditingContact(false);
    setTimeout(() => setContactSaved(false), 3000);
  };

  // Find children linked to this parent — match by linkedStudents array or by guardianName containing parent's last name
  const parentName = currentUser?.name || "";
  const parentLastName = parentName.split(" ").slice(-1)[0];
  const myChildren = students.filter(s => {
    if (currentUser.linkedStudents && currentUser.linkedStudents.length > 0) return currentUser.linkedStudents.includes(s.id);
    return s.guardianName && s.guardianName.toLowerCase().includes(parentLastName.toLowerCase());
  });

  const getChildRoom = (child) => classrooms.find(c => c.id === child.classroomId);
  const getRoomTeachers = (roomId) => teachers.filter(t => !t.isSub && t.assignedRoom === roomId);
  const getAttendanceStatus = (childId) => todayAtt[childId] || "present";
  const statusConfig = { present: { label: "Present", color: COLORS.success, bg: COLORS.successPale, icon: "check" }, absent: { label: "Absent", color: COLORS.danger, bg: COLORS.dangerPale, icon: "x" }, late: { label: "Late", color: COLORS.warning, bg: COLORS.warningPale, icon: "clock" }, "picked-up": { label: "Picked Up", color: COLORS.textMuted, bg: COLORS.borderLight, icon: "home" } };

  // Attendance history for a child
  const getAttHistory = (childId) => {
    const history = [];
    const child = students.find(s => s.id === childId);
    const enrollmentDate = child?.enrollmentDate || "";
    const dates = Object.keys(studentAttendance).sort().reverse().slice(0, 30);
    dates.forEach(d => {
      if (enrollmentDate && d < enrollmentDate) return;
      const dayAtt = studentAttendance[d] || {};
      const st = dayAtt[childId] || "present";
      const dateObj = new Date(d + "T12:00:00");
      if (dateObj.getDay() !== 0 && dateObj.getDay() !== 6) {
        history.push({ date: d, status: st, label: dateObj.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) });
      }
    });
    return history;
  };

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const emoji = "";
  const firstName = parentName.split(" ")[0];

  // Navigation for parent
  const parentNav = [
    { id: "home", label: "Home", icon: "home" },
    { id: "daily-log", label: "Daily Log", icon: "daily-log" },
    { id: "attendance", label: "Attendance", icon: "attendance" },
    { id: "events", label: "Events", icon: "events" },
    { id: "messages", label: "Messages", icon: "messages" },
  ];

  if (view === "messages") {
    return (<div>
      <div style={{ marginBottom: 16 }}><button onClick={() => setView("home")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: COLORS.primary, fontFamily: "inherit", padding: 0 }}>← Back to Home</button></div>
      <MessagesPage messages={messages} setMessages={setMessages} channels={channels} setChannels={setChannels} users={users} currentUser={currentUser} schoolId={schoolId} isDemo={isDemo} />
    </div>);
  }

  if (view === "child" && selectedChild) {
    const child = myChildren.find(c => c.id === selectedChild);
    if (!child) { setView("home"); return null; }
    const room = getChildRoom(child);
    const roomTeachers = room ? getRoomTeachers(room.id) : [];
    const history = getAttHistory(child.id);
    const presentDays = history.filter(h => h.status === "present" || h.status === "late").length;
    const attendanceRate = history.length > 0 ? Math.round((presentDays / history.length) * 100) : 100;

    return (<div>
      <button onClick={() => setView("home")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: COLORS.primary, fontFamily: "inherit", padding: 0, marginBottom: 16 }}>← Back to Home</button>
      <div style={{ background: `linear-gradient(135deg, ${COLORS.primaryPale}, ${COLORS.accentPale})`, borderRadius: 20, padding: "24px 28px", marginBottom: 24, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -10, right: -10, opacity: 0.15 }}><SeasonalArt season={season} size={140} /></div>
        <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 16 }}>
          {child.photo ? <img src={child.photo} alt="" style={{ width: 56, height: 56, borderRadius: "50%", objectFit: "cover", border: "3px solid #fff" }} /> : <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 20, color: COLORS.primary }}>{child.firstName[0]}{child.lastName[0]}</div>}
          <div>
            <h2 style={{ margin: 0, fontSize: 24, fontFamily: "'Nunito', sans-serif", fontWeight: 800, color: COLORS.navy }}>{studentName(child)}</h2>
            <p style={{ margin: "4px 0 0", color: COLORS.textMuted, fontSize: 14 }}>{room?.name || "Unassigned"} · {room?.ageGroup || ""}</p>
          </div>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 16, marginBottom: 24 }}>
        <div style={{ background: COLORS.card, borderRadius: 16, padding: 20, border: `1px solid ${COLORS.border}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: COLORS.navy }}> Student Information</h3>
            {!editingContact && !isDemo && <button onClick={() => openContactEdit(child)} style={{ padding: "5px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, background: COLORS.card, cursor: "pointer", fontSize: 11, fontWeight: 600, color: COLORS.primary, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4 }}><Icon name="edit" size={12} /> Edit</button>}
          </div>
          {contactSaved && <div style={{ fontSize: 12, color: COLORS.success, fontWeight: 600, marginBottom: 10 }}>Contact info updated.</div>}
          {editingContact ? (
            <div style={{ display: "grid", gap: 10 }}>
              <InputField label="Guardian Name" value={contactForm.guardianName} onChange={e => setContactForm(f => ({ ...f, guardianName: e.target.value }))} placeholder="Full name" />
              <InputField label="Phone" value={contactForm.guardianPhone} onChange={e => setContactForm(f => ({ ...f, guardianPhone: e.target.value }))} placeholder="(555) 000-0000" />
              <InputField label="Email" value={contactForm.guardianEmail} onChange={e => setContactForm(f => ({ ...f, guardianEmail: e.target.value }))} placeholder="email@example.com" />
              <InputField label="Home Address" value={contactForm.address} onChange={e => setContactForm(f => ({ ...f, address: e.target.value }))} placeholder="Street, City, State, ZIP" />
              <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                <Button variant="secondary" onClick={() => setEditingContact(false)}>Cancel</Button>
                <Button onClick={() => saveContactInfo(child)} disabled={contactSaving}>{contactSaving ? "Saving..." : "Save Changes"}</Button>
              </div>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 10, fontSize: 13 }}>
              <div><span style={{ color: COLORS.textMuted }}>Full Name:</span> <strong>{studentName(child)}</strong></div>
              <div><span style={{ color: COLORS.textMuted }}>Date of Birth:</span> <strong>{new Date(child.dob + "T12:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</strong></div>
              <div><span style={{ color: COLORS.textMuted }}>Classroom:</span> <strong>{room?.name}</strong> (Room {room?.room})</div>
              {child.address && <div><span style={{ color: COLORS.textMuted }}>Address:</span> <strong>{child.address}</strong></div>}
              <div><span style={{ color: COLORS.textMuted }}>Guardian:</span> <strong>{child.guardianName}</strong></div>
              <div><span style={{ color: COLORS.textMuted }}>Phone:</span> <strong>{child.guardianPhone}</strong></div>
              {child.guardianEmail && <div><span style={{ color: COLORS.textMuted }}>Email:</span> <strong>{child.guardianEmail}</strong></div>}
              {child.allergies && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: COLORS.danger, fontWeight: 700 }}><Icon name="alert" size={12} /> Allergies:</span> <strong style={{ color: COLORS.danger }}>{child.allergies}</strong></div>}
              {child.notes && <div><span style={{ color: COLORS.textMuted }}>Notes:</span> <span>{child.notes}</span></div>}
              {child.authorizedPickup && child.authorizedPickup.length > 0 && (<div style={{ marginTop: 4 }}>
                <span style={{ color: COLORS.textMuted, fontWeight: 600 }}><Icon name="check" size={12} /> Authorized Pickup:</span>
                {child.authorizedPickup.map((p, i) => (
                  <div key={i} style={{ marginTop: 4, paddingLeft: 12, fontSize: 12, display: "flex", gap: 8 }}>
                    <strong>{p.name}</strong><span style={{ color: COLORS.textMuted }}>({p.relation})</span><span style={{ color: COLORS.textMuted }}>{p.phone}</span>
                  </div>
                ))}
              </div>)}
            </div>
          )}
        </div>
        <div style={{ background: COLORS.card, borderRadius: 16, padding: 20, border: `1px solid ${COLORS.border}` }}>
          <h3 style={{ margin: "0 0 14px", fontSize: 14, fontWeight: 800, color: COLORS.navy }}> Classroom Teachers</h3>
          {roomTeachers.length === 0 ? <div style={{ fontSize: 13, color: COLORS.textMuted }}>No teachers assigned</div> : roomTeachers.map(t => (
            <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${COLORS.borderLight}` }}>
              <Avatar initials={t.avatar} size={32} color={COLORS.primary} bg={COLORS.primaryPale} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{t.name}</div>
                <div style={{ fontSize: 11, color: COLORS.textMuted }}>{t.role} · {t.email}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ background: COLORS.card, borderRadius: 16, padding: 20, border: `1px solid ${COLORS.border}` }}>
          <h3 style={{ margin: "0 0 14px", fontSize: 14, fontWeight: 800, color: COLORS.navy }}><Icon name="attendance" size={14} /> Attendance Overview</h3>
          <div style={{ textAlign: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 36, fontWeight: 800, color: attendanceRate >= 90 ? COLORS.success : attendanceRate >= 75 ? COLORS.warning : COLORS.danger, fontFamily: "'Nunito', sans-serif" }}>{attendanceRate}%</div>
            <div style={{ fontSize: 12, color: COLORS.textMuted }}>Last {history.length} school days</div>
          </div>
          <div style={{ display: "flex", gap: 3, flexWrap: "wrap", justifyContent: "center" }}>
            {history.slice(0, 20).reverse().map((h, i) => (
              <div key={i} title={`${h.label}: ${h.status.startsWith("absent") ? "absent" : h.status}`} style={{ width: 18, height: 18, borderRadius: 4, background: h.status === "present" ? COLORS.success : h.status.startsWith("absent") ? COLORS.danger : h.status === "late" ? COLORS.warning : COLORS.textMuted, opacity: 0.8 }} />
            ))}
          </div>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 10, fontSize: 10, color: COLORS.textMuted }}>
            <span><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: COLORS.success, marginRight: 3 }} />Present</span>
            <span><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: COLORS.warning, marginRight: 3 }} />Late</span>
            <span><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: COLORS.danger, marginRight: 3 }} />Absent</span>
          </div>
        </div>
      </div>
      {/* Today's Activities */}
      {(() => {
        const todayEntries = (activityLog[schoolToday] || []).filter(e => e.studentId === child.id).sort((a, b) => a.time > b.time ? 1 : -1);
        const fmtTimeC = (t) => { if (!t) return ""; const [h, m] = t.split(":"); const hr = parseInt(h); return `${hr > 12 ? hr - 12 : hr || 12}:${m} ${hr >= 12 ? "PM" : "AM"}`; };
        return (
          <div style={{ background: COLORS.card, borderRadius: 16, padding: 20, border: `1px solid ${COLORS.border}`, marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: COLORS.navy }}><Icon name="daily-log" size={14} /> Today's Activities</h3>
              <button onClick={() => setView("daily-log")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: COLORS.primary, fontWeight: 700, fontFamily: "inherit" }}>View all days →</button>
            </div>
            {todayEntries.length === 0 ? (
              <div style={{ fontSize: 13, color: COLORS.textMuted, textAlign: "center", padding: 12 }}>No activities logged yet today.</div>
            ) : (<>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                {ACTIVITY_TYPES.map(at => { const c = todayEntries.filter(e => e.type === at.id).length; if (c === 0) return null; return <div key={at.id} style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 8, background: at.color + "15", fontSize: 12, fontWeight: 600, color: at.color }}><Icon name={at.icon} size={12} /> {c}</div>; })}
              </div>
              {todayEntries.map(e => { const t = ACTIVITY_TYPES.find(at => at.id === e.type); return (
                <div key={e.id} style={{ display: "flex", gap: 10, padding: "8px 0", borderBottom: `1px solid ${COLORS.borderLight}`, alignItems: "flex-start" }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: (t?.color || COLORS.textMuted) + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon name={t?.icon} size={15} /></div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: t?.color }}>{t?.label}{e.detail ? ` — ${e.detail}` : ""}</div>
                    {e.note && <div style={{ fontSize: 11, color: COLORS.textMuted, fontStyle: "italic" }}>{e.note}</div>}
                    {e.photoUrl && <img src={e.photoUrl} alt="Activity" style={{ marginTop: 6, maxWidth: 160, maxHeight: 100, borderRadius: 6, objectFit: "cover", cursor: "pointer", border: `1px solid ${COLORS.border}` }} onClick={() => window.open(e.photoUrl, "_blank")} />}
                    <div style={{ fontSize: 10, color: COLORS.textMuted }}>{fmtTimeC(e.time)} · {e.loggedBy}</div>
                  </div>
                </div>
              ); })}
            </>)}
          </div>
        );
      })()}
      <div style={{ background: COLORS.card, borderRadius: 16, padding: 20, border: `1px solid ${COLORS.border}` }}>
        <h3 style={{ margin: "0 0 14px", fontSize: 14, fontWeight: 800, color: COLORS.navy }}><Icon name="attendance" size={14} /> Attendance History</h3>
        <div style={{ maxHeight: 300, overflowY: "auto" }}>
          {history.map((h, i) => { const cfg = statusConfig[h.status] || statusConfig.present; return (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: `1px solid ${COLORS.borderLight}` }}>
              <span style={{ color: cfg.color }}><Icon name={cfg.icon} size={16} /></span>
              <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{h.label}</span>
              <Badge color={cfg.color} bg={cfg.bg}>{cfg.label}</Badge>
            </div>
          ); })}
        </div>
      </div>
    </div>);
  }

  if (view === "daily-log") {
    const dayEntries = activityLog[logDate] || [];
    const fmtTime = (t) => { if (!t) return ""; const [h, m] = t.split(":"); const hr = parseInt(h); return `${hr > 12 ? hr - 12 : hr || 12}:${m} ${hr >= 12 ? "PM" : "AM"}`; };
    return (<div>
      <button onClick={() => setView("home")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: COLORS.primary, fontFamily: "inherit", padding: 0, marginBottom: 16 }}>← Back to Home</button>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 24, fontFamily: "'Nunito', sans-serif", fontWeight: 800, color: COLORS.navy }}><ClipboardList size={24} style={{display:"inline",verticalAlign:"middle",marginRight:8}} />Daily Activity Log</h2>
        <div style={{display:"flex",alignItems:"center",gap:4}}>
              <button onClick={()=>{ const d=new Date(logDate+"T12:00:00"); d.setDate(d.getDate()-1); setLogDate(d.toISOString().split("T")[0]); }} style={{width:32,height:34,borderRadius:8,border:`1px solid ${COLORS.border}`,background:COLORS.card,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:COLORS.text,fontSize:18,flexShrink:0}}>‹</button>
              <input type="date" value={logDate} onChange={e => setLogDate(e.target.value)} style={{ padding:"6px 10px", borderRadius:8, border:`1px solid ${COLORS.border}`, fontSize:13, fontFamily:"inherit", background:COLORS.card, color:COLORS.text, width:148 }} />
              <button onClick={()=>{ const d=new Date(logDate+"T12:00:00"); d.setDate(d.getDate()+1); setLogDate(d.toISOString().split("T")[0]); }} style={{width:32,height:34,borderRadius:8,border:`1px solid ${COLORS.border}`,background:COLORS.card,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:COLORS.text,fontSize:18,flexShrink:0}}>›</button>
            </div>
      </div>
      {myChildren.map(child => {
        const childEntries = dayEntries.filter(e => e.studentId === child.id).sort((a, b) => a.time > b.time ? 1 : -1);
        const room = getChildRoom(child);
        return (
          <div key={child.id} style={{ background: COLORS.card, borderRadius: 16, padding: 20, border: `1px solid ${COLORS.border}`, marginBottom: 16 }}>
            <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>{studentName(child)} <span style={{ fontSize: 12, color: COLORS.textMuted, fontWeight: 500 }}>— {room?.name}</span></h3>
            {childEntries.length === 0 ? (
              <div style={{ padding: "16px 0", fontSize: 13, color: COLORS.textMuted, textAlign: "center" }}>
                {logDate === schoolToday ? "No activities logged yet today. Check back later!" : "No activities were logged on this day."}
              </div>
            ) : (
              <div>
                {/* Activity type summary */}
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                  {ACTIVITY_TYPES.map(at => { const c = childEntries.filter(e => e.type === at.id).length; if (c === 0) return null; return <div key={at.id} style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 8, background: at.color + "15", fontSize: 12, fontWeight: 600, color: at.color }}><Icon name={at.icon} size={12} /> {c}</div>; })}
                </div>
                {childEntries.map(e => {
                  const t = ACTIVITY_TYPES.find(at => at.id === e.type);
                  return (
                    <div key={e.id} style={{ display: "flex", gap: 12, padding: "10px 0", borderBottom: `1px solid ${COLORS.borderLight}`, alignItems: "flex-start" }}>
                      <div style={{ width: 34, height: 34, borderRadius: 10, background: (t?.color || COLORS.textMuted) + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon name={t?.icon} size={17} /></div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: t?.color || COLORS.text }}>{t?.label}</span>
                          {e.detail && <span style={{ fontSize: 12, color: COLORS.textMuted }}>— {e.detail}</span>}
                        </div>
                        {e.note && <div style={{ fontSize: 12, color: COLORS.textMuted, fontStyle: "italic", marginTop: 2 }}>{e.note}</div>}
                        {e.photoUrl && (
                          <div style={{ marginTop: 8 }}>
                            <img src={e.photoUrl} alt="Activity" style={{ maxWidth: 200, maxHeight: 140, borderRadius: 8, objectFit: "cover", border: `1px solid ${COLORS.border}`, cursor: "pointer" }} onClick={() => window.open(e.photoUrl, "_blank")} />
                          </div>
                        )}
                        <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 3 }}>{fmtTime(e.time)} · by {e.loggedBy}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>);
  }

  if (view === "attendance") {
    return (<div>
      <button onClick={() => setView("home")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: COLORS.primary, fontFamily: "inherit", padding: 0, marginBottom: 16 }}>← Back to Home</button>
      <h2 style={{ margin: "0 0 20px", fontSize: 24, fontFamily: "'Nunito', sans-serif", fontWeight: 800, color: COLORS.navy }}><Icon name="attendance" size={14} /> Attendance History</h2>
      {myChildren.map(child => {
        const history = getAttHistory(child.id);
        const room = getChildRoom(child);
        return (
          <div key={child.id} style={{ background: COLORS.card, borderRadius: 16, padding: 20, border: `1px solid ${COLORS.border}`, marginBottom: 16 }}>
            <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>{studentName(child)} <span style={{ fontSize: 12, color: COLORS.textMuted, fontWeight: 500 }}>— {room?.name}</span></h3>
            <div style={{ maxHeight: 200, overflowY: "auto" }}>
              {history.map((h, i) => { const cfg = statusConfig[h.status] || statusConfig.present; return (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 0", borderBottom: `1px solid ${COLORS.borderLight}` }}>
                  <span style={{ color: cfg.color }}><Icon name={cfg.icon} size={14} /></span>
                  <span style={{ flex: 1, fontSize: 13 }}>{h.label}</span>
                  <Badge color={cfg.color} bg={cfg.bg}>{cfg.label}</Badge>
                </div>
              ); })}
              {history.length === 0 && <div style={{ fontSize: 13, color: COLORS.textMuted, padding: 12 }}>No attendance records yet.</div>}
            </div>
          </div>
        );
      })}
    </div>);
  }

  if (view === "events") {
    const upcoming = events.filter(e => new Date(e.date) >= new Date()).sort((a, b) => new Date(a.date) - new Date(b.date));
    return (<div>
      <button onClick={() => setView("home")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: COLORS.primary, fontFamily: "inherit", padding: 0, marginBottom: 16 }}>← Back to Home</button>
      <h2 style={{ margin: "0 0 20px", fontSize: 24, fontFamily: "'Nunito', sans-serif", fontWeight: 800, color: COLORS.navy }}><Icon name="events" size={18} /> Upcoming Events</h2>
      {upcoming.length === 0 && <div style={{ background: COLORS.card, borderRadius: 16, padding: 40, textAlign: "center", border: `1px solid ${COLORS.border}` }}><div style={{ display: "flex", justifyContent: "center", marginBottom: 8, opacity: 0.35 }}><SeasonalArt season={season} size={120} /></div><div style={{ fontSize: 14, color: COLORS.textMuted }}>No upcoming events scheduled.</div></div>}
      {upcoming.map(e => (
        <div key={e.id} style={{ background: COLORS.card, borderRadius: 14, padding: "16px 20px", border: `1px solid ${COLORS.border}`, marginBottom: 12, display: "flex", gap: 16, alignItems: "center" }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: e.type === "holiday" ? COLORS.warningPale : COLORS.primaryPale, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase" }}>{new Date(e.date + "T12:00:00").toLocaleDateString("en-US", { month: "short" })}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: COLORS.navy }}>{new Date(e.date + "T12:00:00").getDate()}</div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{e.title}</div>
            <div style={{ fontSize: 12, color: COLORS.textMuted }}>{e.time} · {e.location}</div>
            {e.description && <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>{e.description}</div>}
          </div>
          <Badge color={e.type === "holiday" ? COLORS.warning : COLORS.primary} bg={e.type === "holiday" ? COLORS.warningPale : COLORS.primaryPale}>{e.type}</Badge>
        </div>
      ))}
    </div>);
  }

  // HOME VIEW
  return (<div>
    {/* Greeting */}
    <div style={{ background: `linear-gradient(135deg, ${COLORS.primaryPale}, ${COLORS.accentPale})`, borderRadius: 20, padding: isMobile ? "20px 16px" : "28px 32px", marginBottom: 24, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: -10, right: -10, opacity: 0.2 }}><SeasonalArt season={season} size={180} /></div>
      <div style={{ position: "relative" }}>
        <h2 style={{ margin: 0, fontSize: 26, fontFamily: "'Nunito', sans-serif", fontWeight: 800, color: COLORS.navy }}>{greeting}, {firstName}! {emoji}</h2>
        <p style={{ margin: "6px 0 0", color: COLORS.textMuted, fontSize: 14, fontWeight: 500 }}>Welcome to the <strong>{schoolSettings.schoolName}</strong> Parent Portal</p>
      </div>
    </div>

    {/* Quick nav */}
    <div style={{ display: "flex", gap: 10, marginBottom: 24, flexWrap: "wrap" }}>
      {parentNav.filter(n => n.id !== "home").map(n => (
        <button key={n.id} onClick={() => setView(n.id)} style={{ padding: "10px 20px", borderRadius: 12, border: `1px solid ${COLORS.border}`, background: COLORS.card, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 700, color: COLORS.text, display: "flex", alignItems: "center", gap: 8, transition: "all 0.15s" }} onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.06)"; }} onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}>
          <Icon name={n.icon} size={16} />{n.label}
        </button>
      ))}
    </div>

    {/* My Children */}
    <h3 style={{ margin: "0 0 16px", fontSize: 18, fontFamily: "'Nunito', sans-serif", fontWeight: 800, color: COLORS.navy, display: "flex", alignItems: "center", gap: 8 }}><Icon name="students" size={18} /> My Children</h3>
    {myChildren.length === 0 ? (
      <div style={{ background: COLORS.card, borderRadius: 16, padding: 40, textAlign: "center", border: `1px solid ${COLORS.border}` }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 12, opacity: 0.4 }}><SeasonalArt season={season} size={140} /></div>
        <div style={{ fontSize: 16, fontWeight: 600, color: COLORS.textMuted }}>No children linked to your account yet</div>
        <div style={{ fontSize: 13, color: COLORS.textMuted, marginTop: 4 }}>Please contact the school administrator to link your child(ren) to your account.</div>
      </div>
    ) : (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16, marginBottom: 28 }}>
        {myChildren.map(child => {
          const room = getChildRoom(child);
          const roomTeachers = room ? getRoomTeachers(room.id) : [];
          const attStatus = getAttendanceStatus(child.id);
          const cfg = statusConfig[attStatus] || statusConfig.present;
          return (
            <div key={child.id} onClick={() => { setSelectedChild(child.id); setView("child"); }} style={{ background: COLORS.card, borderRadius: 18, padding: 20, border: `1px solid ${COLORS.border}`, cursor: "pointer", transition: "transform 0.15s, box-shadow 0.15s" }} onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.08)"; }} onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  {child.photo ? <img src={child.photo} alt="" style={{ width: 42, height: 42, borderRadius: "50%", objectFit: "cover" }} /> : <div style={{ width: 42, height: 42, borderRadius: "50%", background: COLORS.primaryPale, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 15, color: COLORS.primary }}>{child.firstName[0]}{child.lastName[0]}</div>}
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: COLORS.navy }}>{studentName(child)}</div>
                    <div style={{ fontSize: 12, color: COLORS.textMuted }}>{room?.name || "Unassigned"} · {room?.ageGroup || ""}</div>
                  </div>
                </div>
                <Badge color={cfg.color} bg={cfg.bg}><Icon name={cfg.icon} size={11} /> {cfg.label}</Badge>
              </div>
              {child.allergies && <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 8, background: COLORS.dangerPale, marginBottom: 12 }}><span style={{ fontSize: 12, fontWeight: 700, color: COLORS.danger }}><Icon name="alert" size={12} /> {child.allergies}</span></div>}
              {/* Today's activity mini icons */}
              {(() => { const todayE = (activityLog[schoolToday] || []).filter(e => e.studentId === child.id); if (todayE.length === 0) return null; return (
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 10 }}>
                  {ACTIVITY_TYPES.map(at => { const c = todayE.filter(e => e.type === at.id).length; if (c === 0) return null; return <span key={at.id} style={{ display: "flex", alignItems: "center", gap: 3, padding: "2px 8px", borderRadius: 6, background: at.color + "12", fontSize: 11, fontWeight: 600, color: at.color }}><Icon name={at.icon} size={11} />{c}</span>; })}
                </div>
              ); })()}
              <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 8 }}>
                <span style={{ fontWeight: 600 }}>Teachers:</span> {roomTeachers.map(t => t.name).join(", ") || "Not assigned"}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: 11, color: COLORS.textMuted }}>DOB: {new Date(child.dob + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                <span style={{ fontSize: 11, color: COLORS.primary, fontWeight: 700 }}>View details →</span>
              </div>
            </div>
          );
        })}
      </div>
    )}

    {/* Today's Summary */}
    {myChildren.length > 0 && (<>
      <h3 style={{ margin: "0 0 16px", fontSize: 18, fontFamily: "'Nunito', sans-serif", fontWeight: 800, color: COLORS.navy }}><Icon name="reports" size={18} /> Today's Summary</h3>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 28 }}>
        <StatCard label="Children" value={myChildren.length} sub={`${myChildren.filter(c => getAttendanceStatus(c.id) === "present").length} present today`} color={COLORS.primary} iconComponent={<GraduationCap size={40} />} />
        <StatCard label="Classrooms" value={[...new Set(myChildren.map(c => c.classroomId))].length} sub={[...new Set(myChildren.map(c => getChildRoom(c)?.name).filter(Boolean))].join(", ")} color={COLORS.navy} iconComponent={<BookOpen size={40} />} />
        <StatCard label="Upcoming Events" value={events.filter(e => new Date(e.date) >= new Date()).length} sub="View calendar" color={COLORS.accent} iconComponent={<Star size={40} />} onClick={() => setView("events")} />
      </div>
    </>)}

    {/* Upcoming Events Preview */}
    <h3 style={{ margin: "0 0 16px", fontSize: 18, fontFamily: "'Nunito', sans-serif", fontWeight: 800, color: COLORS.navy }}><Icon name="events" size={18} /> Upcoming Events</h3>
    <div style={{ background: COLORS.card, borderRadius: 16, padding: 20, border: `1px solid ${COLORS.border}`, marginBottom: 28, cursor: "pointer" }} onClick={() => setView("events")}>
      {events.filter(e => new Date(e.date) >= new Date()).slice(0, 3).map(e => (
        <div key={e.id} style={{ display: "flex", gap: 14, marginBottom: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: e.type === "holiday" ? COLORS.warningPale : COLORS.primaryPale, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase" }}>{new Date(e.date + "T12:00:00").toLocaleDateString("en-US", { month: "short" })}</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: COLORS.navy }}>{new Date(e.date + "T12:00:00").getDate()}</div>
          </div>
          <div><div style={{ fontSize: 14, fontWeight: 600 }}>{e.title}</div><div style={{ fontSize: 12, color: COLORS.textMuted }}>{e.time} · {e.location}</div></div>
        </div>
      ))}
      {events.filter(e => new Date(e.date) >= new Date()).length === 0 && <div style={{ fontSize: 13, color: COLORS.textMuted }}>No upcoming events.</div>}
      <div style={{ fontSize: 11, color: COLORS.primary, fontWeight: 700, marginTop: 8 }}>View all events →</div>
    </div>

    {/* Check-in PIN */}
    <h3 style={{ margin: "0 0 16px", fontSize: 18, fontFamily: "'Nunito', sans-serif", fontWeight: 800, color: COLORS.navy }}><Icon name="attendance" size={18} /> Kiosk Check-In</h3>
    <PinSetupCard currentUser={currentUser} />

    {/* Notification Preferences */}
    {schoolSettings.digestEnabled && (<>
    <h3 style={{ margin: "0 0 16px", fontSize: 18, fontFamily: "'Nunito', sans-serif", fontWeight: 800, color: COLORS.navy }}><Icon name="bell" size={18} /> Notification Preferences</h3>
    <div style={{ background: COLORS.card, borderRadius: 16, padding: 20, border: `1px solid ${COLORS.border}`, marginBottom: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.navy }}>Daily digest email</div>
          <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>Receive a daily summary of your child's attendance and activities</div>
        </div>
        <div onClick={async () => {
          const newVal = !currentUser.digestOptOut;
          currentUser.digestOptOut = newVal;
          await supabase.from("profiles").update({ digest_opt_out: newVal }).eq("id", currentUser.id);
        }} style={{ width: 44, height: 24, borderRadius: 12, background: currentUser.digestOptOut ? COLORS.border : COLORS.primary, cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0, marginLeft: 16 }}>
          <div style={{ position: "absolute", top: 3, left: currentUser.digestOptOut ? 3 : 23, width: 18, height: 18, borderRadius: 9, background: "#fff", transition: "left 0.2s" }} />
        </div>
      </div>
    </div>
    </>)}

    {/* School Contact */}
    <h3 style={{ margin: "0 0 16px", fontSize: 18, fontFamily: "'Nunito', sans-serif", fontWeight: 800, color: COLORS.navy }}><Icon name="phone" size={18} /> School Contact</h3>
    <div style={{ background: COLORS.card, borderRadius: 16, padding: 20, border: `1px solid ${COLORS.border}` }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, fontSize: 13 }}>
        <div><span style={{ color: COLORS.textMuted }}>Phone:</span> <strong>{schoolSettings.phone}</strong></div>
        <div><span style={{ color: COLORS.textMuted }}>Email:</span> <strong>{schoolSettings.email}</strong></div>
        <div><span style={{ color: COLORS.textMuted }}>Address:</span> <strong>{schoolSettings.address}</strong></div>
        <div><span style={{ color: COLORS.textMuted }}>Director:</span> <strong>{schoolSettings.principal}</strong></div>
      </div>
    </div>
  </div>);
});

export default ParentPortal;
